import { useTRPC } from '@/utils/trpc';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { LabDetail } from '@liza/ui/components/records';
import {
  classifyObservation,
  getLabDisplayName,
  getObservationIdentifier,
  shouldExcludeObservation,
} from '@liza/ui/utils/observation';
import { formatCodeableConcept, resolveId, type WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  labIdentifier: string;
  medplumPatientId: string;
  /** Navigate to the lab order (`ServiceRequest`) a result came from, when one is linked. */
  onSelectLabOrder?: (serviceRequestId: string) => void;
};

type LabOrderLink = { serviceRequestId: string; display?: string };

/**
 * Resolve the lab order each result came from. A lab-result `Observation` has no back-reference
 * to its order, so we walk forward through the report. HG nests each result leaf under a value-less
 * `hg-observaton-group` wrapper that `DiagnosticReport.result[]` points at, so two batched searches
 * (constant, regardless of how many draws this lab has) resolve every link:
 *   1. `DiagnosticReport?result.has-member=<all leaf ids>` → report.basedOn (the order) + report.result (group ids)
 *   2. `Observation?has-member=<all leaf ids>` → each group wrapper's `hasMember` (the leaf ids it nests)
 * Joining them gives leaf → group → report → order. Manually-entered labs and results with no linked
 * report are simply absent from the map (no link renders). The `result.has-member` chain only matches
 * the HG nested shape; a flat report that lists a leaf directly in `result[]` is not covered yet.
 * See `docs/design-decisions/2026-06-22-modeling-lab-orders-and-results.md`.
 */
const useResultLabOrders = (results: WithId<Observation>[], medplumPatientId: string): Record<string, LabOrderLink> => {
  const trpc = useTRPC();
  const leafRefs = results.map((obs) => `Observation/${obs.id}`).join(',');
  const enabled = leafRefs.length > 0;

  const reportsQuery = useQuery({
    ...trpc.patient.diagnosticReport.list.queryOptions({
      medplumPatientId,
      options: { searchParams: { 'result.has-member': leafRefs, _elements: 'id,basedOn,result' } },
    }),
    enabled,
  });

  const groupsQuery = useQuery({
    ...trpc.patient.observation.list.queryOptions({
      category: 'labs',
      medplumPatientId,
      options: { searchParams: { 'has-member': leafRefs, _elements: 'id,hasMember' } },
    }),
    enabled,
  });

  return useMemo(() => {
    const leafIds = new Set(results.map((obs) => obs.id));

    // group id → order, from each report's `result[]` group refs + `basedOn`
    const groupToOrder = new Map<string, LabOrderLink>();
    for (const report of reportsQuery.data?.diagnosticReports ?? []) {
      const basedOn = report.basedOn?.[0];
      const serviceRequestId = basedOn ? resolveId(basedOn) : undefined;
      if (!serviceRequestId) continue;
      for (const ref of report.result ?? []) {
        const groupId = resolveId(ref);
        if (groupId) groupToOrder.set(groupId, { serviceRequestId, display: basedOn?.display });
      }
    }

    // leaf id → order, via the group wrapper that nests it
    const links: Record<string, LabOrderLink> = {};
    for (const group of groupsQuery.data?.observations ?? []) {
      const order = group.id ? groupToOrder.get(group.id) : undefined;
      if (!order) continue;
      for (const member of group.hasMember ?? []) {
        const leafId = resolveId(member);
        if (leafId && leafIds.has(leafId)) links[leafId] = order;
      }
    }
    return links;
  }, [reportsQuery.data, groupsQuery.data, results]);
};

export const LabDetailContent = ({ labIdentifier, medplumPatientId, onSelectLabOrder }: Props): React.ReactNode => {
  const { t } = useTranslation('common');
  const trpc = useTRPC();

  const codeQuery = useQuery(
    trpc.patient.observation.list.queryOptions({
      category: 'labs',
      medplumPatientId,
      options: { status: 'active', searchParams: { code: labIdentifier } },
    }),
  );

  // Uncoded, text-only labs get the formatted code text as their identifier, which a token search
  // can't match — retry against the code's text.
  const isTextIdentifier = codeQuery.isSuccess && codeQuery.data.observations.length === 0;
  const textQuery = useQuery({
    ...trpc.patient.observation.list.queryOptions({
      category: 'labs',
      medplumPatientId,
      options: { status: 'active', searchParams: { 'code:text': labIdentifier } },
    }),
    enabled: isTextIdentifier,
  });

  const observationsQuery = isTextIdentifier ? textQuery : codeQuery;

  const labResults = useMemo(
    () =>
      (observationsQuery.data?.observations ?? []).filter(
        (obs) =>
          classifyObservation(obs) === 'result' &&
          !shouldExcludeObservation(formatCodeableConcept(obs.code)) &&
          (getObservationIdentifier(obs) === labIdentifier || formatCodeableConcept(obs.code) === labIdentifier),
      ),
    [observationsQuery.data, labIdentifier],
  );

  const resultOrderLinks = useResultLabOrders(labResults, medplumPatientId);

  return (
    <QueryStateHandler query={observationsQuery} emptyMessage={t('noRecordsAvailable')}>
      {() => {
        if (labResults.length === 0) return <p className="p-4">{t('labNotFound')}</p>;
        const labName = getLabDisplayName(labResults[0]!.code);
        return (
          <LabDetail
            labName={labName}
            results={labResults}
            resultOrderLinks={resultOrderLinks}
            onSelectLabOrder={onSelectLabOrder}
          />
        );
      }}
    </QueryStateHandler>
  );
};
